<script setup>
import { computed, ref, defineProps, watch, defineEmits } from 'vue';

const props = defineProps({
	content: {
		type: Object,
		required: true,
	},

	disabled: {
		type: Boolean,
		default: false,
	},

	response: {
		type: Array,
		required: false,
	},

	clickableRows: {
		type: Boolean,
		default: false,
	},

	selectedRow: {
		type: String,
		default: null,
	},

	groupName: {
		type: [String, Number],
		default: 'default',
	},
});

const emit = defineEmits(['select-row']);

const columns = computed(() => props.content?.columns ?? []);

const rows = computed(() => props.content?.rows ?? []);

const selectedByRow = computed(() => {
	const map = new Map();

	if (!Array.isArray(props.response)) {
		return map;
	}
	for (const item of props.response) {
		const rowIndex = rows.value.indexOf(item?.row);

		const colIndex = columns.value.indexOf(item?.column);

		if (rowIndex >= 0 && colIndex >= 0) {
			map.set(rowIndex, colIndex);
		}
	}
	return map;
});

const selections = ref([]);

watch(
	selectedByRow,

	(next) => {
		const values = [];
		next.forEach((colIndex, rowIndex) => {
			values[rowIndex] = colIndex;
		});
		selections.value = values;
	},

	{ immediate: true }
);

const columnCount = computed(() => columns.value.length);

const gridTemplateColumns = computed(() => {
	const radioCols = columnCount.value
		? ` repeat(${columnCount.value}, minmax(5rem, 5rem))`
		: '';

	return `minmax(0, 1fr)${radioCols}`;
});

const radioName = (rowIndex) => `grid-row-${props.groupName}-${rowIndex}`;

const isSelected = (rowIndex, colIndex) =>
	selectedByRow.value.get(rowIndex) === colIndex;
</script>



<template>
	<div class="mt-4 overflow-x-auto p-0.5">
		<div class="flex flex-col gap-2 min-w-[20rem]">
			<div class="grid" :style="{ gridTemplateColumns }">
				<div />

				<div
					v-for="(column, colIndex) in columns"
					:key="`header-${colIndex}`"
					class="text-neutral-700 text-center justify-self-center pb-2"
				>
					{{ column }}
				</div>
			</div>

			<div
				v-for="(row, rowIndex) in rows"
				:key="`row-${rowIndex}`"
				class="grid rounded-lg"
				:style="{ gridTemplateColumns }"
				:class="[
					clickableRows && disabled ? 'cursor-pointer' : '',
					clickableRows && disabled && selectedRow === row
						? 'bg-yellow-50 border-2 border-yellow-600'
						: 'bg-neutral-50',
				]"
				@click="clickableRows && disabled && emit('select-row', row)"
			>
				<div
					class="text-neutral-700 px-4 py-4 flex items-center min-h-full"
				>
					{{ row }}
				</div>

				<div
					v-for="(column, colIndex) in columns"
					:key="`cell-${rowIndex}-${colIndex}`"
					class="flex items-center justify-center py-4 min-h-full"
				>
					<span
						v-if="props.disabled"
						class="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2"
						:class="
							isSelected(rowIndex, colIndex)
								? 'border-yellow-600'
								: 'border-neutral-400'
						"
						:aria-label="`${row} - ${column}`"
					>
						<span
							v-if="isSelected(rowIndex, colIndex)"
							class="h-2 w-2 rounded-full bg-yellow-600"
						/>
					</span>

					<input
						v-else
						type="radio"
						:name="radioName(rowIndex)"
						:value="colIndex"
						v-model="selections[rowIndex]"
						class="h-4 w-4 shrink-0 text-yellow-600 border-neutral-400 border-2 focus:ring-yellow-600 cursor-pointer"
						:aria-label="`${row} - ${column}`"
						@click.stop
					/>
				</div>
			</div>
		</div>
	</div>
</template>

